import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bot, RotateCcw, ChevronLeft, Clock } from "lucide-react";
import { AssistantChat } from "./AssistantChat";
import { SessionHistoryList } from "./SessionHistoryList";
import { useVagaAssistant } from "./useVagaAssistant";
import { DropCandidateModal } from "@/components/vagas/kanban/DropCandidateModal";
import { KanbanAddOrigin, useKanbanApi } from "@/utils/kanban-api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FreelancerStatus } from "@/types/freelancer-rating";
import type { KanbanCard } from "@/types/kanban";
import type { FreelancerSugerido } from "./useVagaAssistant";
import { reportUserError } from "@/lib/report-error";
import { trackEvent } from "@/lib/telemetry";

interface VagaAssistantPanelProps {
  vagaId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VagaAssistantPanel({
  vagaId,
  open,
  onOpenChange,
}: VagaAssistantPanelProps) {
  const {
    messages,
    isLoading,
    sessionId,
    suggestions,
    sugestoesAtivas,
    loadingSuggestions,
    loadingSession,
    sendMessage,
    retryLastMessage,
    cancelStream,
    clearMessages,
    loadSession,
  } = useVagaAssistant(vagaId, open);

  const queryClient = useQueryClient();
  const kanbanApi = useKanbanApi();
  const { user } = useAuth();
  const { toast } = useToast();
  const [view, setView] = useState<"history" | "chat">("history");
  const [isProcessing, setIsProcessing] = useState(false);
  const [dropFreelancer, setDropFreelancer] = useState<FreelancerSugerido | null>(null);
  const [isDropping, setIsDropping] = useState(false);

  useEffect(() => {
    if (open) trackEvent("assistente.abrir", { contexto: { contexto_tipo: "vaga", vaga_id: vagaId } });
  }, [open, vagaId]);

  const trackCardAction = (item: "card_selecionados" | "card_shortlist" | "card_descartar", freelancerId: string) =>
    trackEvent("assistente.card_acao", { entidade: { tipo: "freelancer", id: freelancerId }, contexto: { item, vaga_id: vagaId } });

  // Buscar etapas do kanban
  const { data: etapas = [] } = useQuery({
    queryKey: ["kanban-etapas", vagaId],
    queryFn: () => kanbanApi.getEtapas(vagaId),
    enabled: open && !!vagaId,
    staleTime: 5 * 60 * 1000,
  });

  // Derivar etapas relevantes
  const etapaSelecionados = useMemo(
    () => etapas.find((e) => e.tipo === "ativa" && /selecionad/i.test(e.nome)),
    [etapas]
  );
  const etapaShortlist = useMemo(
    () => etapas.find((e) => e.tipo === "ativa" && /shortlist/i.test(e.nome)),
    [etapas]
  );
  const etapaDrop = useMemo(
    () => etapas.find((e) => e.tipo === "drop"),
    [etapas]
  );

  // Invalidar caches do kanban
  const invalidateKanban = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["disponibilidade"] });
    queryClient.invalidateQueries({ queryKey: ["kanban-cards", vagaId] });
    queryClient.invalidateQueries({ queryKey: ["kanban-metricas", vagaId] });
  }, [queryClient, vagaId]);

  // Quando enviar mensagem, ir para o chat e invalidar cache do histórico
  const handleSendMessage = async (message: string) => {
    if (view !== "chat") setView("chat");
    await sendMessage(message);
    queryClient.invalidateQueries({ queryKey: ["vaga-assistant-sessions"] });
  };

  // Se já tem mensagens, mostrar chat
  useEffect(() => {
    if (messages.length > 0) {
      setView("chat");
    }
  }, [messages.length]);

  const handleSelectSession = async (selectedSessionId: string, selectedVagaId: string) => {
    await loadSession(selectedSessionId, selectedVagaId);
    setView("chat");
  };

  const handleNewConversation = () => {
    clearMessages();
    setView("chat");
  };

  const handleBackToHistory = () => {
    setView("history");
  };

  // Handler: Adicionar em Selecionados
  const handleSelecionados = async (freelancerId: string) => {
    if (!etapaSelecionados) {
      toast({ title: "Erro", description: "Etapa 'Selecionados' não encontrada.", variant: "destructive" });
      return;
    }
    setIsProcessing(true);
    try {
      await kanbanApi.addCandidate(vagaId, freelancerId, etapaSelecionados.id, {
        origem: KanbanAddOrigin.AssistenteCard,
        usuarioId: user?.id,
      });
      trackCardAction("card_selecionados", freelancerId);
      invalidateKanban();
      toast({ title: "Sucesso", description: "Freelancer adicionado em Selecionados!" });
    } catch (error: any) {
      const isDuplicate = error?.code === "23505" || error?.message?.includes("já está");
      toast({
        title: "Erro",
        description: isDuplicate
          ? "Este freelancer já está nesta vaga."
          : "Não foi possível adicionar. " + reportUserError(error, { source: "assistente-vaga" }).description,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler: Adicionar na Shortlist
  const handleShortlist = async (freelancerId: string) => {
    if (!etapaShortlist) {
      toast({ title: "Erro", description: "Etapa 'Shortlist' não encontrada.", variant: "destructive" });
      return;
    }
    setIsProcessing(true);
    try {
      await kanbanApi.addCandidate(vagaId, freelancerId, etapaShortlist.id, {
        origem: KanbanAddOrigin.AssistenteCard,
        usuarioId: user?.id,
      });
      trackCardAction("card_shortlist", freelancerId);
      invalidateKanban();
      toast({ title: "Sucesso", description: "Freelancer adicionado na Shortlist!" });
    } catch (error: any) {
      const isDuplicate = error?.code === "23505" || error?.message?.includes("já está");
      toast({
        title: "Erro",
        description: isDuplicate
          ? "Este freelancer já está nesta vaga."
          : "Não foi possível adicionar. " + reportUserError(error, { source: "assistente-vaga" }).description,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler: Abrir modal de descarte. Recebe o freelancer completo do card
  // (não depende de uma lista em memória, então funciona em qualquer página).
  const handleDescartar = (freelancer: FreelancerSugerido) => {
    setDropFreelancer(freelancer);
  };

  // Handler: Confirmar descarte (adiciona + dropa)
  const handleDropConfirmed = async (data: {
    motivos: string[];
    observacoes?: string;
    status?: FreelancerStatus;
  }) => {
    if (!dropFreelancer || !etapaDrop) return;

    setIsDropping(true);
    try {
      // Primeiro: adicionar na primeira etapa ativa (precisa existir no kanban para drop)
      const etapaInicial = etapaSelecionados || etapas.find((e) => e.tipo === "ativa");
      if (!etapaInicial) throw new Error("Nenhuma etapa ativa encontrada.");

      await kanbanApi.addCandidate(vagaId, dropFreelancer.id, etapaInicial.id, {
        origem: KanbanAddOrigin.AssistenteCard,
        usuarioId: user?.id,
      });

      // Buscar posição recém-criada
      const cards = await kanbanApi.getCards(vagaId);
      const posicao = cards.find((c) => c.freelancer_id === dropFreelancer.id);
      if (!posicao) throw new Error("Posição do candidato não encontrada.");

      // Dropar
      await kanbanApi.dropCard(posicao.id, etapaDrop.id, data.motivos, data.observacoes, user?.id);

      // Se motivo inclui status de disponibilidade, atualizar
      if (data.status) {
        const hoje = new Date().toISOString().split("T")[0];
        await kanbanApi.updateFreelancerDisponibilidade(
          dropFreelancer.id,
          hoje,
          data.status,
          data.observacoes,
          user?.id
        );
      }

      trackCardAction("card_descartar", dropFreelancer.id);
      invalidateKanban();
      toast({ title: "Sucesso", description: "Freelancer descartado." });
    } catch (error: any) {
      const isDuplicate = error?.code === "23505" || error?.message?.includes("já está");
      toast({
        title: "Erro",
        description: isDuplicate
          ? "Este freelancer já está nesta vaga."
          : "Não foi possível descartar. " + reportUserError(error, { source: "assistente-vaga" }).description,
        variant: "destructive",
      });
    } finally {
      setIsDropping(false);
      setDropFreelancer(null);
    }
  };

  // Criar KanbanCard mock para o DropCandidateModal
  const dropCandidateForModal: KanbanCard | null = dropFreelancer
    ? {
        id: "",
        vaga_id: vagaId,
        freelancer_id: dropFreelancer.id,
        etapa_atual_id: "",
        ordem_na_etapa: 0,
        data_adicao: new Date().toISOString(),
        is_dropped: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        freelancer: {
          id: dropFreelancer.id,
          nome: dropFreelancer.nome,
          email: "",
          areas: dropFreelancer.areas?.join(", ") || "",
        },
        etapa: {
          id: "",
          vaga_id: vagaId,
          nome: "",
          ordem: 0,
          cor: "#8B5CF6",
          tipo: "ativa",
          is_template_default: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        total_movimentacoes: 0,
      }
    : null;

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[560px] lg:w-[780px] xl:w-[900px] sm:max-w-none p-0 flex flex-col"
      >
        <SheetHeader className="px-4 py-3 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {view === "chat" && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={handleBackToHistory}
                  title="Histórico"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              {view === "history" ? (
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <Clock className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <SheetTitle className="text-sm">Assistente IA</SheetTitle>
                    <p className="text-xs text-muted-foreground">
                      Histórico de conversas
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <Bot className="h-4.5 w-4.5 text-purple-600" />
                  </div>
                  <div>
                    <SheetTitle className="text-sm">Assistente IA</SheetTitle>
                    <p className="text-xs text-muted-foreground">
                      Seleção de freelancers
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              {view === "chat" && sessionId && (
                <Badge variant="outline" className="text-[10px]">
                  Sessão ativa
                </Badge>
              )}
              {view === "chat" && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleNewConversation}
                  title="Nova conversa"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        {view === "history" ? (
          <SessionHistoryList
            onSelectSession={handleSelectSession}
            onNewConversation={handleNewConversation}
            currentVagaId={vagaId}
          />
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            {loadingSession ? (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full mr-2" />
                Carregando conversa...
              </div>
            ) : (
              <AssistantChat
                messages={messages}
                isLoading={isLoading}
                onSendMessage={handleSendMessage}
                onRetry={retryLastMessage}
                onCancel={cancelStream}
                suggestions={suggestions}
                sugestoesAtivas={sugestoesAtivas}
                loadingSuggestions={loadingSuggestions}
                onSelecionados={etapaSelecionados ? handleSelecionados : undefined}
                onShortlist={etapaShortlist ? handleShortlist : undefined}
                onDescartar={etapaDrop ? handleDescartar : undefined}
                isProcessing={isProcessing}
              />
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>

      {/* Modal de descarte — renderizado FORA do Sheet de propósito. Um Dialog
          modal aninhado DENTRO do Sheet (que também é modal) não era
          apresentado pelo Radix: o focus-trap/scroll-lock do Sheet engolia o
          modal e "nada aparecia" ao clicar em Descartar. Como irmão do Sheet
          ele abre por cima normalmente. */}
      <DropCandidateModal
        open={!!dropFreelancer}
        onOpenChange={(open) => { if (!open) setDropFreelancer(null); }}
        candidate={dropCandidateForModal}
        onConfirm={handleDropConfirmed}
        isLoading={isDropping}
      />
    </>
  );
}
